import { useEffect } from 'react';
import { Loader2, Mic, RotateCcw, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useVoiceInput } from './useVoiceInput';
import { reportTranscribe } from './reportTranscribe';

/**
 * A mic on any single field.
 *
 * The composers have had voice since Sprint A, but the two places a tech is
 * fixing ONE word they already know — the 3C edit fields and the help bubble —
 * had none. Those are the moments where typing on a tablet with gloves on is
 * most annoying and the dictation is shortest.
 *
 * The button is also the retry. A clip that failed to transcribe is held, and
 * rather than a second control (or a toast action, which outlives the screen
 * that recorded it), the same button turns into "try again" until the tech
 * either gets the words back or throws the clip away. `anchorKey` drops the
 * clip when the field is pointed at something else — these fields do not
 * remount when the 3C version changes underneath them.
 */
export interface VoiceDictateButtonProps {
  /** Receives the transcript. Callers append rather than replace. */
  onText: (text: string) => void;
  disabled?: boolean;
  /**
   * Glyph size: compact for inline field use, default suits a composer row.
   * The tap target is 44px either way — only the drawn icon changes.
   */
  size?: 'sm' | 'md';
  /** What this field belongs to — a held clip is dropped when it changes. */
  anchorKey?: string | null;
  label?: string;
}

export function VoiceDictateButton({
  onText,
  disabled = false,
  size = 'sm',
  anchorKey,
  label = 'Dictate',
}: VoiceDictateButtonProps) {
  const voice = useVoiceInput();
  const recording = voice.state === 'recording';
  const transcribing = voice.state === 'transcribing';
  const held = voice.hasHeldClip && !recording && !transcribing;

  useEffect(() => {
    return () => voice.discardClip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey]);

  async function onClick() {
    if (transcribing) return;
    if (recording) {
      const outcome = await voice.stopAndTranscribe();
      if (outcome.text) onText(outcome.text);
      else reportTranscribe(outcome);
      return;
    }
    if (held) {
      const outcome = await voice.retryTranscribe();
      if (outcome.text) onText(outcome.text);
      else if (outcome.message) toast.message(outcome.message);
      return;
    }
    await voice.start();
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => void onClick()}
        disabled={disabled || transcribing}
        aria-label={recording ? 'Stop and transcribe' : held ? 'Try that recording again' : label}
        aria-pressed={recording}
        title={recording ? 'Stop' : held ? 'That recording is still here — try again' : label}
        className={cn(
          'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border transition-colors duration-150 disabled:opacity-40',
          recording
            ? 'border-fw-danger bg-fw-danger text-fw-danger-fg'
            : held
              ? 'border-fw-warning/50 bg-fw-warning-soft text-fw-warning'
              : 'border-fw-border bg-fw-surface text-fw-text-secondary hover:bg-fw-surface-hover hover:text-fw-text',
        )}
      >
        {transcribing ? (
          <Loader2 size={size === 'sm' ? 13 : 15} className="animate-spin" aria-hidden />
        ) : recording ? (
          <Square
            size={size === 'sm' ? 11 : 13}
            strokeWidth={2.5}
            fill="currentColor"
            aria-hidden
          />
        ) : held ? (
          <RotateCcw size={size === 'sm' ? 13 : 15} strokeWidth={2} aria-hidden />
        ) : (
          <Mic size={size === 'sm' ? 13 : 15} strokeWidth={1.9} aria-hidden />
        )}
      </button>
      {/* Only while a failed clip is held — otherwise the tech is stuck being
          offered a retry when what they want is to record something new. */}
      {held && (
        <button
          type="button"
          onClick={voice.discardClip}
          aria-label="Throw that recording away"
          title="Throw it away"
          className="text-fw-text-muted hover:text-fw-text inline-flex h-11 w-11 items-center justify-center rounded transition-colors duration-150"
        >
          <X size={12} strokeWidth={2.2} aria-hidden />
        </button>
      )}
    </span>
  );
}

/** Appends dictated text to what is already in a field. */
export function appendDictation(existing: string, dictated: string): string {
  const a = existing.trimEnd();
  const b = dictated.trim();
  if (!b) return existing;
  if (!a) return b;
  return `${a} ${b}`;
}
